import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { JobOpening } from '../types/api';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';
import DocumentTitle from '../components/DocumentTitle';
import MetaDescription from '../components/MetaDescription';

const PublicJobsPage: React.FC = () => {
  const [jobs, setJobs] = useState<JobOpening[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const jobsData = await apiService.getActiveJobs();
        setJobs(jobsData);
        setError(null);
      } catch (err) {
        setError('Failed to load jobs');
        console.error('Error fetching jobs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">Error</h1>
            <p className="mt-4 text-gray-600">{error}</p>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <DocumentTitle title="Jobs" />
      <MetaDescription description="Browse our current job openings and find your next career opportunity. Join our team and be part of something great." />
      <PublicHeader />

      {/* Main Content */}
      <main>
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold text-gray-900 sm:text-5xl">
              Open Positions
            </h1>
            <p className="mt-4 text-xl text-gray-600">
              Join our team and be part of something great
            </p>
          </div>

          {jobs.length === 0 ? (
            <div className="mt-16 text-center">
              <h2 className="text-2xl font-semibold text-gray-900">No Open Positions</h2>
              <p className="mt-4 text-gray-600">
                We don't have any open positions at the moment. Please check back later!
              </p>
            </div>
          ) : (
            <div className="mt-16 grid gap-8 lg:grid-cols-2 xl:grid-cols-3">
              {jobs.map((job) => (
                <div key={job.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{job.title}</h3>
                  {job.location && (
                    <p className="text-sm text-gray-500 mb-3">📍 {job.location}</p>
                  )}
                  {job.salary_range && (
                    <p className="text-sm text-gray-500 mb-3">💰 {job.salary_range}</p>
                  )}
                  <div className="text-gray-600 text-sm mb-4 line-clamp-3">
                    {job.description.replace(/<[^>]*>/g, '').substring(0, 150)}...
                  </div>
                  <div className="flex">
                    <Link
                      to={`/jobs/${job.id}/apply`}
                      className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Apply Now
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default PublicJobsPage;
